import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import TestReportForm from "./TestReportForm";
import { generateProfessionalPDF } from "../utils/pdfGenerator";
import Toast from "./Toast";

interface TestReport {
  id: string;
  orderId: string;
  projectName: string;
  clientName: string;
  date: string;
  gradeOfConcrete: string;
  status: "approved" | "pending" | "in-progress";
  avgStrength: string;
  vehicleNumber?: string;
  createdAt: string;
}

interface OrderManagementViewProps {
  onBack?: () => void;
  onClose?: () => void;
}

export default function OrderManagementView({
  onBack,
  onClose,
}: OrderManagementViewProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | "pending" | "completed" | "draft">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showToast, setShowToast] = useState<{ message: string; type: "success" | "info" | "warning" } | null>(null);
  const [reports, setReports] = useState([
    {
      id: "1",
      orderId: "ORD-2024-001",
      customerName: "ABC Construction Ltd",
      location: "Site A - North Wing",
      grade: "M30",
      date: "Dec 23, 2024",
      status: "completed" as const,
      cubes: 3,
    },
    {
      id: "2",
      orderId: "ORD-2024-002",
      customerName: "XYZ Builders Pvt Ltd",
      location: "Site B - Foundation",
      grade: "M25",
      date: "Dec 22, 2024",
      status: "pending" as const,
      cubes: 6,
    },
    {
      id: "3",
      orderId: "ORD-2024-003",
      customerName: "Prime Infrastructure",
      location: "Site C - Column Casting",
      grade: "M30",
      date: "Dec 21, 2024",
      status: "completed" as const,
      cubes: 4,
    },
    {
      id: "4",
      orderId: "ORD-2024-004",
      customerName: "Delta Constructions",
      location: "Site D - Slab Work",
      grade: "M20",
      date: "Dec 20, 2024",
      status: "draft" as const,
      cubes: 2,
    },
  ]);

  const handleFormSubmit = (formData: any) => {
    // Create new test report
    const newReport: TestReport = {
      id: `TR-${String(reports.length + 1).padStart(3, "0")}`,
      orderId: formData.orderId,
      projectName: formData.projectName,
      clientName: formData.clientName,
      date: formData.date,
      gradeOfConcrete: formData.gradeOfConcrete,
      status: "in-progress",
      avgStrength:
        formData.tests[0]?.avgCompressiveStrength ||
        formData.tests[0]?.compressiveStrength ||
        "-",
      vehicleNumber: formData.vehicleDetails?.vehicleNumber,
      createdAt: new Date().toISOString(),
    };

    setReports((prev) => [newReport, ...prev]);
    setShowForm(false);
    setShowToast({
      message: "Test report created successfully",
      type: "success",
    });

    // Auto-generate PDF
    setTimeout(() => {
      generateProfessionalPDF(formData);
      setShowToast({ message: "PDF report downloaded", type: "success" });
    }, 1000);
  };

  const handleDownloadReport = (report: TestReport) => {
    // Mock data for PDF generation
    const mockData = {
      orderId: report.orderId,
      projectName: report.projectName,
      clientName: report.clientName,
      docRefNo: `DOC-${report.id}`,
      date: report.date,
      cementType: "OPC 53 Grade",
      cubeSize: "150mm×150mm×150mm",
      gradeOfConcrete: report.gradeOfConcrete,
      tests: [
        {
          srNo: 1,
          location: "Slab-1",
          slumpMm: "85",
          cubeIdMark: "C-001",
          dateOfCasting: report.date,
          dateOfTesting: new Date(
            new Date(report.date).getTime() + 28 * 24 * 60 * 60 * 1000
          )
            .toISOString()
            .split("T")[0],
          timeOfTesting: "10:30",
          ageOfCube: "28 days",
          weightOfCube: "8250",
          crushingLoad: "760.5",
          compressiveStrength: report.avgStrength,
          avgCompressiveStrength: report.avgStrength,
        },
      ],
      vehicleDetails: report.vehicleNumber
        ? {
            vehicleNumber: report.vehicleNumber,
            driverName: "Driver Name",
            departureTime: "09:00",
          }
        : undefined,
      remarks: "All tests completed successfully. Concrete meets specifications.",
      signatures: {
        engineerQAQC: "Engineer Name",
        managerQAQC: "Manager Name",
        projectManager: "PM Name",
      },
    };

    generateProfessionalPDF(mockData);
    setShowToast({ message: "Report downloaded successfully", type: "success" });
  };

  const filteredReports =
    filterStatus === "all"
      ? reports
      : reports.filter((r) => r.status === filterStatus);

  const statusColors = {
    approved: { bg: "#10B98108", text: "#10B981", border: "#10B98130" },
    pending: { bg: "#34D39908", text: "#34D399", border: "#34D39930" },
    "in-progress": { bg: "#6EE7B708", text: "#6EE7B7", border: "#6EE7B730" },
    completed: { bg: "#05966908", text: "#059669", border: "#05966930" },
    draft: { bg: "#A7F3D008", text: "#A7F3D0", border: "#A7F3D030" },
  } as const;

  if (showForm) {
    return (
      <TestReportForm
        orderId={selectedOrder || ""}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setShowForm(false);
          setSelectedOrder(null);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1B2A5B] to-[#0A0F1C] text-white px-5 md:px-8 lg:px-12 pt-8 pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              {(onBack || onClose) && (
                <button
                  onClick={onBack || onClose}
                  className="w-10 h-10 rounded-full backdrop-blur-xl flex items-center justify-center"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 4l-6 6 6 6"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
              <h1
                style={{
                  fontSize: "clamp(24px, 5vw, 32px)",
                  letterSpacing: "-0.03em",
                  fontWeight: "800",
                }}
              >
                Test Reports
              </h1>
            </div>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowForm(true)}
              className="px-4 py-2 rounded-xl backdrop-blur-xl flex items-center gap-2"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                fontSize: "13px",
                fontWeight: "700",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 3V13M3 8H13"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span className="hidden sm:inline">New Report</span>
            </motion.button>
          </div>
          <p
            className="text-white/70"
            style={{
              fontSize: "14px",
              letterSpacing: "0.01em",
              fontWeight: "500",
            }}
          >
            Manage concrete cube test reports
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="px-5 md:px-8 lg:px-12 py-5">
        <div className="max-w-7xl mx-auto">
          <div
            className="bg-white p-1.5 rounded-2xl flex gap-2 overflow-x-auto"
            style={{
              boxShadow:
                "0 2px 8px rgba(0, 0, 0, 0.04), 0 12px 24px rgba(0, 0, 0, 0.06)",
            }}
          >
            {[
              { id: "all", label: "All", count: reports.length },
              {
                id: "pending",
                label: "Pending",
                count: reports.filter((r) => r.status === "pending").length,
              },
              {
                id: "completed",
                label: "Completed",
                count: reports.filter((r) => r.status === "completed").length,
              },
              {
                id: "draft",
                label: "Draft",
                count: reports.filter((r) => r.status === "draft").length,
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() =>
                  setFilterStatus(
                    tab.id as "all" | "pending" | "completed" | "draft"
                  )
                }
                className="flex-1 py-3 px-3 rounded-xl relative transition-all"
                style={{
                  color: filterStatus === tab.id ? "white" : "#717182",
                  fontSize: "13px",
                  fontWeight: "700",
                }}
              >
                {filterStatus === tab.id && (
                  <motion.div
                    layoutId="filterTab"
                    className="absolute inset-0 rounded-xl"
                    style={{
                      background:
                        "linear-gradient(135deg, #1B2A5B 0%, #2A3F7A 100%)",
                      boxShadow: "0 4px 12px rgba(27, 42, 91, 0.3)",
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 30,
                    }}
                  />
                )}
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {tab.label}
                  <span
                    className="px-1.5 py-0.5 rounded-md"
                    style={{
                      backgroundColor:
                        filterStatus === tab.id
                          ? "rgba(255, 255, 255, 0.2)"
                          : "#F9FAFB",
                      fontSize: "11px",
                    }}
                  >
                    {tab.count}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="px-5 md:px-8 lg:px-12 space-y-4">
        {filteredReports.map((report, index) => (
          <motion.div
            key={report.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.08, duration: 0.5 }}
            className="bg-white rounded-3xl p-6"
            style={{
              boxShadow:
                "0 2px 8px rgba(0, 0, 0, 0.04), 0 12px 24px rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3
                    className="text-[#0A0F1C]"
                    style={{ fontSize: "18px", fontWeight: "800" }}
                  >
                    {report.orderId}
                  </h3>
                  <div
                    className="px-2 py-1 rounded-md"
                    style={{
                      backgroundColor: "#1B2A5B15",
                      color: "#1B2A5B",
                      fontSize: "10px",
                      fontWeight: "700",
                    }}
                  >
                    {report.gradeOfConcrete}
                  </div>
                </div>
                <p
                  className="text-[#0A0F1C] mb-1"
                  style={{ fontSize: "15px", fontWeight: "600" }}
                >
                  {report.projectName}
                </p>
                <p
                  className="text-[#717182]"
                  style={{ fontSize: "13px", fontWeight: "500" }}
                >
                  {report.clientName}
                </p>
              </div>

              <div
                className="px-3 py-1.5 rounded-full flex items-center gap-1.5"
                style={{
                  backgroundColor: statusColors[report.status].bg,
                  border: `1px solid ${statusColors[report.status].border}`,
                }}
              >
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: statusColors[report.status].text }}
                />
                <span
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.05em",
                    fontWeight: "700",
                    color: statusColors[report.status].text,
                  }}
                >
                  {report.status.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: "#F9FAFB" }}
              >
                <p
                  className="text-[#717182] mb-1"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  DATE
                </p>
                <p
                  className="text-[#0A0F1C]"
                  style={{ fontSize: "13px", fontWeight: "700" }}
                >
                  {new Date(report.date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              </div>

              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: "#F9FAFB" }}
              >
                <p
                  className="text-[#717182] mb-1"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  STRENGTH
                </p>
                <p
                  className="text-[#0A0F1C]"
                  style={{ fontSize: "13px", fontWeight: "700" }}
                >
                  {report.avgStrength} {report.avgStrength !== "-" && "MPa"}
                </p>
              </div>

              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: "#F9FAFB" }}
              >
                <p
                  className="text-[#717182] mb-1"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  VEHICLE
                </p>
                <p
                  className="text-[#0A0F1C]"
                  style={{ fontSize: "13px", fontWeight: "700" }}
                >
                  {report.vehicleNumber || "-"}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => handleDownloadReport(report)}
                className="py-3 px-4 rounded-xl flex items-center justify-center gap-2"
                style={{
                  background:
                    "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "700",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 11V3M8 11L5 8M8 11L11 8M3 13H13"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Download PDF
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() =>
                  setShowToast({
                    message: `Viewing details for ${report.orderId}`,
                    type: "info",
                  })
                }
                className="py-3 px-4 rounded-xl"
                style={{
                  backgroundColor: "white",
                  border: "2px solid #E5E7EB",
                  color: "#717182",
                  fontSize: "14px",
                  fontWeight: "700",
                }}
              >
                View Details
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredReports.length === 0 && (
        <div className="px-5 md:px-8 lg:px-12 py-12 text-center">
          <div
            className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: "#F9FAFB" }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 8H20L24 12V24H8V8Z"
                stroke="#717182"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20 8V12H24"
                stroke="#717182"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3
            className="text-[#0A0F1C] mb-2"
            style={{ fontSize: "18px", fontWeight: "700" }}
          >
            No reports found
          </h3>
          <p
            className="text-[#717182] mb-6"
            style={{ fontSize: "14px", fontWeight: "500" }}
          >
            Create your first test report to get started
          </p>
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowForm(true)}
            className="px-6 py-3 rounded-xl text-white"
            style={{
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              fontSize: "14px",
              fontWeight: "700",
            }}
          >
            Create New Report
          </motion.button>
        </div>
      )}

      {/* Toast */}
      <AnimatePresence>
        {showToast && (
          <div className="fixed bottom-20 left-0 right-0 flex justify-center z-[90] px-5">
            <Toast
              message={showToast.message}
              type={showToast.type}
              onClose={() => setShowToast(null)}
            />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}